import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import EpsfPageBreadcrumb from "../epsf/EpsfPageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import ConfirmModal from "../../common/ConfirmModal";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import { useUserData } from "../../../hooks/useUserData";
import {
  canAddMilestonePlan,
  canDeleteMilestonePlan,
  canEditMilestonePlan,
} from "./isfPermissionUtils";
import {
  createMilestonePlanDraftVersion,
  deleteMilestonePlan,
  extractMilestonePlanActionResult,
  extractMilestoneApiMessage,
  fetchMilestoneLookup,
  fetchMilestonePlanById,
  fetchMilestonePlanList,
  fetchMilestonePlanUsageTemplates,
  normalizeMilestoneLookup,
  normalizeMilestonePlanDetail,
  fetchPlanMilestoneItemsForPopup,
  normalizeMilestonePlanList,
  normalizeMilestonePlanUsageTemplates,
  publishMilestonePlan,
  saveMilestonePlan,
  milestoneCategoryClassName,
  statusBadgeClass,
  updateMilestonePlan,
} from "./isfMilestoneService";
import {
  fetchFolderLibrary,
  folderLibraryToOptions,
  isFolderLibrarySuccess,
  normalizeFolderCategory,
  normalizeFolderLibraryList,
} from "./isfFolderLibraryService";
import { resolveMilestoneFolderCategory } from "./milestoneLibraryShared";
import "./milestoneLibrary.scss";
import {
  ISF_REPORT_ACTIONS_COLUMN,
  ISF_REPORT_PINNED_COLUMNS,
  IsfReportRowActions,
  matchTextColumnSearch,
} from "./isfReportTable";
import "./isfReportTable.scss";

const MILESTONE_PLAN_FIELD_OPTIONS = [
  { key: "PlanName", label: "Plan Name" },
  { key: "MilestoneCount", label: "Milestones" },
  { key: "UsedInTemplates", label: "Used In Templates" },
  { key: "VersionNo", label: "Version" },
  { key: "Status", label: "Status" },
  { key: "SystemDateTime", label: "Created" },
  { key: ISF_REPORT_ACTIONS_COLUMN, label: "Actions" },
];

const DEFAULT_MILESTONE_PLAN_COLUMNS = [
  "PlanName",
  "MilestoneCount",
  "UsedInTemplates",
  "VersionNo",
  "Status",
  "SystemDateTime",
  ISF_REPORT_ACTIONS_COLUMN,
];

const MilestonePlan = ({ lockedCategory = null } = {}) => {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const lock = lockedCategory
    ? String(lockedCategory).trim()
    : null;

  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [milestoneOptions, setMilestoneOptions] = useState([]);
  const [folderOptions, setFolderOptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [planStatus, setPlanStatus] = useState("");
  const [editingId, setEditingId] = useState("");
  const [planName, setPlanName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedMilestoneIds, setSelectedMilestoneIds] = useState([]);
  const [milestoneSearch, setMilestoneSearch] = useState("");
  const [planVersion, setPlanVersion] = useState("");
  const [draftPlanId, setDraftPlanId] = useState("");
  const [errors, setErrors] = useState({});
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    row: null,
    action: null,
  });
  const [planMilestonesModal, setPlanMilestonesModal] = useState({
    open: false,
    plan: null,
    items: [],
  });
  const [planTemplatesModal, setPlanTemplatesModal] = useState({
    open: false,
    plan: null,
    templates: [],
  });

  const isDraft = String(planStatus).toLowerCase() === "draft";
  const isPublished = String(planStatus).toLowerCase() === "published";
  const isCreateMode = modalMode === "create";
  const formReadOnly = modalMode === "view" || isPublished;

  const nextDraftVersion = (planVersion || "1.0").replace(
    /(\d+)$/,
    (_, n) => Number(n) + 1,
  );

  const canAdd = userData && canAddMilestonePlan(userData);
  const canEdit = userData && canEditMilestonePlan(userData);
  const canDelete = userData && canDeleteMilestonePlan(userData);
  const canManage = canAdd || canEdit || canDelete;

  const canSavePlan =
    (isCreateMode ? canAdd : canEdit) && !formReadOnly && (isCreateMode || isDraft);

  const loadList = async () => {
    if (!userData?.OrganizationId) return;
    try {
      setListLoading(true);
      const response = await fetchMilestonePlanList({ userData });
      if (response?.success === 1) {
        setRows(normalizeMilestonePlanList(response));
      } else {
        setRows([]);
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Failed to load milestone plans.",
        type: "danger",
      });
      setRows([]);
    } finally {
      setListLoading(false);
    }
  };

  const loadMilestoneOptions = async () => {
    if (!userData?.OrganizationId) return;
    try {
      const [milestoneResponse, folderResponse] = await Promise.all([
        fetchMilestoneLookup({ userData }),
        fetchFolderLibrary({ userData }),
      ]);
      if (milestoneResponse?.success === 1) {
        setMilestoneOptions(normalizeMilestoneLookup(milestoneResponse));
      }
      if (isFolderLibrarySuccess(folderResponse)) {
        setFolderOptions(
          folderLibraryToOptions(normalizeFolderLibraryList(folderResponse)),
        );
      } else {
        setFolderOptions([]);
      }
    } catch {
      setMilestoneOptions([]);
      setFolderOptions([]);
    }
  };

  useEffect(() => {
    if (userData?.OrganizationId) {
      loadList();
      loadMilestoneOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId]);

  const resetModal = () => {
    setModalMode("create");
    setPlanStatus("");
    setPlanVersion("");
    setDraftPlanId("");
    setEditingId("");
    setPlanName("");
    setDescription("");
    setSelectedMilestoneIds([]);
    setMilestoneSearch("");
    setErrors({});
  };

  const loadPlanIntoModal = async (row, options = {}) => {
    const planId = resolvePlanId(row);
    if (!planId) return;
    const targetPlanId = options.planId || planId;
    try {
      showLoader("Loading plan...");
      const response = await fetchMilestonePlanById({
        userData,
        milestonePlanId: targetPlanId,
      });
      const { detail, items } = normalizeMilestonePlanDetail(response);
      if (!detail) throw new Error("Plan not found.");
      const status = detail.Status || "";
      const isPlanDraft = String(status).toLowerCase() === "draft";
      setModalMode(options.mode || (isPlanDraft ? "edit" : "view"));
      setEditingId(detail.MilestonePlanId);
      setPlanName(detail.PlanName || "");
      setDescription(detail.Description || "");
      setPlanStatus(status);
      setPlanVersion(detail.VersionNo || "");
      setDraftPlanId(
        String(detail.DraftMilestonePlanId || detail.draftMilestonePlanId || "").trim(),
      );
      setSelectedMilestoneIds(
        (items || []).map((i) => String(i.MilestoneId)).filter(Boolean),
      );
      setMilestoneSearch("");
      setErrors({});
      setShowModal(true);
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Failed to load plan.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const openCreate = () => {
    resetModal();
    setModalMode("create");
    setShowModal(true);
  };

  const openView = (row) => loadPlanIntoModal(row);

  const openDraftPlan = () => {
    if (!draftPlanId) return;
    loadPlanIntoModal({ MilestonePlanId: draftPlanId }, { planId: draftPlanId, mode: "edit" });
  };

  const toggleMilestone = (milestoneId) => {
    const id = String(milestoneId);
    setSelectedMilestoneIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const filteredMilestoneOptions = useMemo(() => {
    const scoped = lock
      ? milestoneOptions.filter(
          (m) =>
            resolveMilestoneFolderCategory(m, folderOptions) ===
            normalizeFolderCategory(lock),
        )
      : milestoneOptions;
    const query = milestoneSearch.trim().toLowerCase();
    if (!query) return scoped;
    return scoped.filter((m) => {
      const haystack = [
        m.MilestoneCode,
        m.MilestoneName,
        m.Category,
        m.Version,
        m.Anchor,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [milestoneOptions, milestoneSearch, lock, folderOptions]);

  const displayMilestoneOptions = useMemo(() => {
    if (modalMode !== "view") return filteredMilestoneOptions;
    const selectedSet = new Set(selectedMilestoneIds);
    return filteredMilestoneOptions.filter((m) =>
      selectedSet.has(String(m.MilestoneId)),
    );
  }, [modalMode, filteredMilestoneOptions, selectedMilestoneIds]);

  const renderCountBadge = (count, { clickable, onClick, ariaLabel }) => {
    const value = Number(count) || 0;
    const isClickable = clickable && value > 0;
    return (
      <button
        type="button"
        className={`milestone-used-plans-badge${
          isClickable
            ? " milestone-used-plans-badge--clickable"
            : " milestone-used-plans-badge--zero"
        }`}
        onClick={isClickable ? onClick : undefined}
        disabled={!isClickable}
        title={isClickable ? ariaLabel : undefined}
        aria-label={ariaLabel}
      >
        {value}
      </button>
    );
  };

  const resolvePlanId = (row) =>
    String(row?.MilestonePlanId || row?.milestonePlanId || "").trim();

  const openPlanMilestones = useCallback(async (row) => {
    const count = Number(row?.MilestoneCount) || 0;
    const planId = resolvePlanId(row);
    if (!planId || count <= 0) return;
    try {
      showLoader("Loading milestones...");
      const result = await fetchPlanMilestoneItemsForPopup({
        userData,
        milestonePlanId: planId,
      });
      if (!result.ok) {
        showAlert({
          title: "Error",
          message: result.message || "Failed to load milestones.",
          type: "danger",
        });
        return;
      }
      setPlanMilestonesModal({
        open: true,
        plan: row,
        items: result.items || [],
      });
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Failed to load milestones.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  }, [userData, showLoader, hideLoader, showAlert]);

  const openPlanTemplates = useCallback(async (row) => {
    const count = Number(row?.UsedInTemplates) || 0;
    const planId = resolvePlanId(row);
    if (!planId || count <= 0) return;
    try {
      showLoader("Loading templates...");
      const response = await fetchMilestonePlanUsageTemplates({
        userData,
        milestonePlanId: planId,
      });
      if (response?.success !== 1) {
        showAlert({
          title: "Error",
          message: extractMilestoneApiMessage(response, "Failed to load templates."),
          type: "danger",
        });
        return;
      }
      setPlanTemplatesModal({
        open: true,
        plan: row,
        templates: normalizeMilestonePlanUsageTemplates(response),
      });
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Failed to load templates.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  }, [userData, showLoader, hideLoader, showAlert]);

  const allFilteredSelected =
    filteredMilestoneOptions.length > 0 &&
    filteredMilestoneOptions.every((m) =>
      selectedMilestoneIds.includes(String(m.MilestoneId)),
    );

  const toggleSelectAllFiltered = () => {
    const filteredIds = filteredMilestoneOptions.map((m) => String(m.MilestoneId));
    if (allFilteredSelected) {
      const filteredSet = new Set(filteredIds);
      setSelectedMilestoneIds((prev) => prev.filter((id) => !filteredSet.has(id)));
      return;
    }
    setSelectedMilestoneIds((prev) => [...new Set([...prev, ...filteredIds])]);
  };

  const validate = () => {
    const next = {};
    if (!planName.trim()) next.planName = "Plan name is required.";
    if (!selectedMilestoneIds.length) {
      next.milestones = "Select at least one milestone.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || (isCreateMode ? !canAdd : !canEdit)) return;

    const planItems = selectedMilestoneIds.map((id, i) => ({
      MilestoneId: id,
      SortOrder: i,
    }));

    const payload = {
      MilestonePlanId: editingId || undefined,
      PlanName: planName.trim(),
      Description: description.trim(),
      PlanItems: planItems,
    };

    try {
      showLoader(editingId ? "Updating..." : "Saving...");
      const response = editingId
        ? await updateMilestonePlan({ userData, payload })
        : await saveMilestonePlan({ userData, payload });

      if (response?.success === 1) {
        showAlert({
          title: "Success",
          message: editingId ? "Plan updated." : "Plan saved.",
          type: "success",
        });
        setShowModal(false);
        resetModal();
        await loadList();
      } else {
        showAlert({
          title: "Error",
          message: response?.message || "Save failed.",
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Save failed.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handlePublish = async () => {
    if (!editingId || !canManage || !isDraft) return;

    const authRes = await openAuthModal({
      showDescription: true,
      requireDescription: true,
    });
    if (authRes?.Success !== 1) return;

    const reason = String(authRes?.Description || "").trim();
    if (!reason) return;

    try {
      showLoader("Publishing...");
      const response = await publishMilestonePlan({
        userData,
        milestonePlanId: editingId,
        reason,
      });
      if (response?.success === 1) {
        showAlert({ title: "Success", message: "Plan published.", type: "success" });
        setShowModal(false);
        resetModal();
        await loadList();
      } else {
        showAlert({
          title: "Error",
          message: response?.message || "Publish failed.",
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Publish failed.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const openPublishConfirm = () => {
    if (!editingId || !canManage || !isDraft) return;
    setConfirmModal({ open: true, row: null, action: "publish" });
  };

  const handleCreateDraftVersion = async () => {
    if (!editingId || !canManage || !isPublished) return;
    try {
      showLoader("Creating draft version...");
      const response = await createMilestonePlanDraftVersion({
        userData,
        milestonePlanId: editingId,
      });
      const result = extractMilestonePlanActionResult(response);
      const targetId = result?.MilestonePlanId || result?.milestonePlanId;
      if (response?.success === 1 && targetId) {
        showAlert({
          title: "Success",
          message: result?.MSG || "Draft version ready.",
          type: "success",
        });
        await loadPlanIntoModal(
          { MilestonePlanId: targetId },
          { planId: targetId, mode: "edit" },
        );
        await loadList();
      } else {
        showAlert({
          title: "Error",
          message: response?.message || "Could not create draft version.",
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Could not create draft version.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const openCreateDraftConfirm = () => {
    if (!editingId || !canManage || !isPublished) return;
    setConfirmModal({ open: true, row: null, action: "createDraft" });
  };

  const handleDeleteConfirm = async () => {
    const row = confirmModal.row;
    if (!row?.MilestonePlanId) return;
    setConfirmModal({ open: false, row: null, action: null });

    const authRes = await openAuthModal({
      showDescription: true,
      requireDescription: true,
    });
    if (authRes?.Success !== 1) return;

    const reason = String(authRes?.Description || "").trim();
    if (!reason) return;

    try {
      showLoader("Deleting...");
      const response = await deleteMilestonePlan({
        userData,
        milestonePlanId: row.MilestonePlanId,
        reason,
      });
      if (response?.success === 1) {
        showAlert({ title: "Success", message: "Plan deleted.", type: "success" });
        await loadList();
      } else {
        showAlert({
          title: "Error",
          message: response?.message || "Delete failed.",
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Delete failed.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const tableActions = useMemo(
    () => [
      { label: "View", onClick: openView },
      {
        label: "Delete",
        onClick: (row) => setConfirmModal({ open: true, row, action: "delete" }),
        visible: (row) => canDelete && Number(row.UsedInTemplates) === 0,
      },
    ],
    [canDelete],
  );

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === ISF_REPORT_ACTIONS_COLUMN) {
        return <IsfReportRowActions row={row} actions={tableActions} />;
      }
      if (field === "MilestoneCount") {
        return renderCountBadge(row.MilestoneCount, {
          clickable: true,
          onClick: () => openPlanMilestones(row),
          ariaLabel:
            Number(row.MilestoneCount) > 0
              ? `${row.MilestoneCount} milestone(s) — click to view`
              : "No milestones",
        });
      }
      if (field === "UsedInTemplates") {
        return renderCountBadge(row.UsedInTemplates, {
          clickable: true,
          onClick: () => openPlanTemplates(row),
          ariaLabel:
            Number(row.UsedInTemplates) > 0
              ? `${row.UsedInTemplates} template(s) — click to view`
              : "Not used in any template",
        });
      }
      if (field === "Status") {
        return <span className={statusBadgeClass(row.Status)}>{row.Status}</span>;
      }
      return String(row[field] ?? "—");
    },
    [tableActions, openPlanMilestones, openPlanTemplates],
  );

  const matchColumnSearch = useCallback(
    (row, field, searchValue) =>
      matchTextColumnSearch(row, field, searchValue, [
        ISF_REPORT_ACTIONS_COLUMN,
        "MilestoneCount",
        "UsedInTemplates",
        "Status",
        "SystemDateTime",
      ]),
    [],
  );

  const getFieldLabel = useCallback(
    (field) =>
      MILESTONE_PLAN_FIELD_OPTIONS.find((option) => option.key === field)?.label ||
      field,
    [],
  );

  const layoutStorageKey = userData?.OrganizationId
    ? `isf-milestone-plan-report-${userData.OrganizationId}`
    : "";

  const confirmModalConfig =
    confirmModal.action === "delete"
      ? {
          title: "Delete milestone plan",
          message: `Delete plan "${confirmModal.row?.PlanName || ""}"? You will be asked to confirm your identity and provide a reason.`,
          confirmText: "Continue",
          confirmButtonClass: "btn-danger",
        }
      : confirmModal.action === "publish"
        ? {
            title: "Publish milestone plan",
            message: `Publish "${planName || ""}" v${planVersion || "1.0"}? Once published, this plan can be assigned to templates. You will be asked to provide a reason.`,
            confirmText: "Continue",
            confirmButtonClass: "btn-success",
          }
        : confirmModal.action === "createDraft"
          ? {
              title: "Create draft version",
              message: `Create a new draft from "${planName || ""}" v${planVersion || "1.0"}? A new draft (v${nextDraftVersion}) will be created for editing. The current published version will stay unchanged.`,
              confirmText: "Create draft",
              confirmButtonClass: "btn-primary",
            }
          : null;

  const handleConfirmAction = async () => {
    const action = confirmModal.action;
    if (action === "delete") {
      await handleDeleteConfirm();
      return;
    }
    setConfirmModal({ open: false, row: null, action: null });
    if (action === "createDraft") {
      await handleCreateDraftVersion();
    } else if (action === "publish") {
      await handlePublish();
    }
  };

  const CrumbBar = lock === "ePSF" ? EpsfPageBreadcrumb : PageBreadcrumb;

  return (
    <div className="milestone-page">
      <CrumbBar
        items={
          lock
            ? [
                { label: "ePSF", path: "/epsf/imp/inventory" },
                { label: "Milestone plan" },
              ]
            : [
                { label: "Home", path: "/notification-center" },
                { label: "Milestone Plan" },
              ]
        }
        actions={[
          { icon: "fas fa-refresh", label: "Refresh", onClick: loadList },
          ...(canAdd
            ? [{ icon: "fas fa-plus", label: "Add Plan", onClick: openCreate }]
            : []),
        ]}
      />

      <div className="isf-report-table-wrap">
        <ReportTable
          rows={rows}
          fieldOptions={MILESTONE_PLAN_FIELD_OPTIONS}
          defaultColumns={DEFAULT_MILESTONE_PLAN_COLUMNS}
          getFieldLabel={getFieldLabel}
          renderCell={renderReportCell}
          matchColumnSearch={matchColumnSearch}
          pageSizeOptions={[10, 25, 50, 100]}
          columnSearchMode="client"
          pinnedRightColumns={ISF_REPORT_PINNED_COLUMNS}
          pinnedRightColumnWidthRem={4.5}
          hideAddColumnFields={[ISF_REPORT_ACTIONS_COLUMN]}
          hideColumnHeaderActionsFields={[ISF_REPORT_ACTIONS_COLUMN]}
          persistLayoutKey={layoutStorageKey}
          loading={listLoading}
        />
      </div>

      {showModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg milestone-plan-modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalMode === "create"
                    ? "Add Milestone Plan"
                    : isPublished
                      ? `View Milestone Plan${planVersion ? ` v${planVersion}` : ""}`
                      : `Edit Milestone Plan${planVersion ? ` v${planVersion}` : ""}`}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowModal(false);
                    resetModal();
                  }}
                />
              </div>
              <div className="modal-body milestone-plan-modal-body">
                {isPublished && (
                  <div className="milestone-detail-page__published-banner mb-3">
                    This version is published and read-only. Click{" "}
                    <strong>Create draft version</strong> to edit — a new draft (e.g. v
                    {nextDraftVersion}) will be created.
                    {draftPlanId ? (
                      <>
                        {" "}
                        A draft is already in progress — use <strong>Open draft</strong>.
                      </>
                    ) : null}
                  </div>
                )}
                <div className="row g-3 milestone-plan-modal-layout">
                  <div className="col-lg-5 milestone-plan-modal-left">
                    <div>
                      <label className="milestone-modal-label">
                        Plan name <span className="mandatory">*</span>
                      </label>
                      <input
                        className="form-control form-control-sm"
                        value={planName}
                        disabled={formReadOnly}
                        onChange={(e) => setPlanName(e.target.value)}
                      />
                      {errors.planName && (
                        <small className="text-danger">{errors.planName}</small>
                      )}
                    </div>
                    <div className="milestone-plan-modal-left__description">
                      <label className="milestone-modal-label">Description</label>
                      <textarea
                        className="form-control form-control-sm"
                        value={description}
                        disabled={formReadOnly}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-7 milestone-plan-modal-right">
                    <div className="milestone-plan-milestones-panel">
                      <div className="milestone-plan-milestones-panel__header">
                        <div>
                          <label className="milestone-modal-label mb-0">
                            Select milestones <span className="mandatory">*</span>
                          </label>
                          {errors.milestones && (
                            <small className="text-danger d-block">
                              {errors.milestones}
                            </small>
                          )}
                        </div>
                        {selectedMilestoneIds.length > 0 && (
                          <span className="milestone-plan-milestones-panel__count">
                            {selectedMilestoneIds.length} selected
                          </span>
                        )}
                      </div>
                      <div className="milestone-plan-milestones-panel__toolbar">
                        <div className="milestone-plan-milestones-panel__search">
                          <i className="fas fa-search" aria-hidden="true" />
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search code, name, anchor…"
                            value={milestoneSearch}
                            onChange={(e) => setMilestoneSearch(e.target.value)}
                          />
                        </div>
                        {filteredMilestoneOptions.length > 0 && !formReadOnly && (
                          <button
                            type="button"
                            className="milestone-plan-milestones-panel__select-all"
                            onClick={toggleSelectAllFiltered}
                          >
                            {allFilteredSelected ? "Deselect all" : "Select all"}
                          </button>
                        )}
                      </div>
                      <div className="milestone-plan-milestones-panel__scroll">
                        {milestoneOptions.length === 0 ? (
                          <p className="milestone-plan-milestones-panel__empty">
                            No published milestones. Create and publish milestones in
                            the Milestone Library first.
                          </p>
                        ) : displayMilestoneOptions.length === 0 ? (
                          <p className="milestone-plan-milestones-panel__empty">
                            {formReadOnly
                              ? "No milestones in this plan."
                              : "No milestones match your search."}
                          </p>
                        ) : (
                          displayMilestoneOptions.map((m) => {
                            const isSelected = selectedMilestoneIds.includes(
                              String(m.MilestoneId),
                            );
                            return (
                              <label
                                key={m.MilestoneId}
                                className={`milestone-plan-milestone-row${
                                  isSelected ? " milestone-plan-milestone-row--selected" : ""
                                }${formReadOnly ? " milestone-plan-milestone-row--readonly" : ""}`}
                              >
                                <input
                                  type="checkbox"
                                  className="milestone-plan-milestone-row__input"
                                  checked={isSelected}
                                  disabled={formReadOnly}
                                  onChange={() => toggleMilestone(m.MilestoneId)}
                                />
                                <span
                                  className="milestone-plan-milestone-row__check"
                                  aria-hidden="true"
                                >
                                  {isSelected && <i className="fas fa-check" />}
                                </span>
                                <span className="milestone-plan-milestone-row__content">
                                  <span className="milestone-plan-milestone-row__meta">
                                    <span className="milestone-plan-milestone-row__code">
                                      {m.MilestoneCode}
                                    </span>
                                    {m.Category ? (
                                      <span
                                        className={milestoneCategoryClassName(m.Category)}
                                      >
                                        {m.Category}
                                      </span>
                                    ) : null}
                                  </span>
                                  <span className="milestone-plan-milestone-row__detail">
                                    <span className="milestone-plan-milestone-row__name">
                                      {m.MilestoneName}
                                    </span>
                                    {m.Anchor ? (
                                      <span className="milestone-plan-milestone-row__anchor">
                                        {m.Anchor}
                                      </span>
                                    ) : null}
                                  </span>
                                </span>
                                {m.Version ? (
                                  <span className="milestone-plan-milestone-row__version">
                                    v{m.Version}
                                  </span>
                                ) : null}
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetModal();
                  }}
                >
                  {formReadOnly ? "Close" : "Cancel"}
                </button>
                {isPublished && canManage && !draftPlanId && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={openCreateDraftConfirm}
                  >
                    Create draft version
                  </button>
                )}
                {isPublished && canManage && draftPlanId && (
                  <button type="button" className="btn btn-primary" onClick={openDraftPlan}>
                    Open draft
                  </button>
                )}
                {canManage && isDraft && editingId && (
                  <button type="button" className="btn btn-success" onClick={openPublishConfirm}>
                    Publish
                  </button>
                )}
                {canSavePlan && (
                  <button type="button" className="btn btn-primary" onClick={handleSave}>
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModalConfig?.title || ""}
        message={confirmModalConfig?.message || ""}
        confirmText={confirmModalConfig?.confirmText || "Confirm"}
        confirmButtonClass={confirmModalConfig?.confirmButtonClass || "btn-primary"}
        onConfirm={handleConfirmAction}
        onClose={() => setConfirmModal({ open: false, row: null, action: null })}
      />

      {planMilestonesModal.open && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Plan milestones</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() =>
                    setPlanMilestonesModal({ open: false, plan: null, items: [] })
                  }
                />
              </div>
              <div className="modal-body milestone-plan-milestones-preview-modal">
                <p className="milestone-usage-plans-modal__subtitle">
                  <strong>{planMilestonesModal.plan?.PlanName}</strong>
                </p>
                {planMilestonesModal.items.length === 0 ? (
                  <p className="text-muted mb-0">No milestones found.</p>
                ) : (
                  <div className="milestone-plan-milestones-preview">
                    {planMilestonesModal.items.map((item) => (
                      <div
                        key={item.PlanItemId || item.MilestoneId}
                        className="milestone-plan-milestone-row milestone-plan-milestone-row--display"
                      >
                        <span className="milestone-plan-milestone-row__content">
                          <span className="milestone-plan-milestone-row__meta">
                            <span className="milestone-plan-milestone-row__code">
                              {item.MilestoneCode}
                            </span>
                            {item.Category ? (
                              <span className={milestoneCategoryClassName(item.Category)}>
                                {item.Category}
                              </span>
                            ) : null}
                          </span>
                          <span className="milestone-plan-milestone-row__detail">
                            <span className="milestone-plan-milestone-row__name">
                              {item.MilestoneName}
                            </span>
                            {item.Anchor ? (
                              <span className="milestone-plan-milestone-row__anchor">
                                {item.Anchor}
                              </span>
                            ) : null}
                          </span>
                        </span>
                                {item.MilestoneVersion || item.Version ? (
                                  <span className="milestone-plan-milestone-row__version">
                                    v{item.MilestoneVersion || item.Version}
                                  </span>
                                ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() =>
                    setPlanMilestonesModal({ open: false, plan: null, items: [] })
                  }
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {planTemplatesModal.open && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Used in templates</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() =>
                    setPlanTemplatesModal({ open: false, plan: null, templates: [] })
                  }
                />
              </div>
              <div className="modal-body milestone-usage-plans-modal">
                <p className="milestone-usage-plans-modal__subtitle">
                  <strong>{planTemplatesModal.plan?.PlanName}</strong>
                </p>
                {planTemplatesModal.templates.length === 0 ? (
                  <p className="text-muted mb-0">No templates found.</p>
                ) : (
                  <ul className="milestone-usage-plans-modal__list">
                    {planTemplatesModal.templates.map((tpl) => (
                      <li key={tpl.TemplateId}>
                        <span className="milestone-usage-plans-modal__name">
                          {tpl.TemplateName}
                        </span>
                        {tpl.TemplateCategory ? (
                          <span className="text-muted">{tpl.TemplateCategory}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() =>
                    setPlanTemplatesModal({ open: false, plan: null, templates: [] })
                  }
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
    </div>
  );
};

export default MilestonePlan;
